import {
    Chip,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useHistory } from 'react-router-dom';
import { ROUTE_HOME } from 'src/config/routes';
import { AppRoute } from 'src/core/AppRoute';

export const ToolbarItemIconButton = styled(IconButton)(({ theme }) => ({
    marginTop: 'auto',
    marginBottom: 'auto',
    marginRight: theme.spacing(0.5),
    color: 'inherit',
    textDecoration: 'none',
}));

const StyledList = styled(List)(({ theme }) => ({
    '& [class*="MuiListItemText-root"]': {
        paddingLeft: theme.spacing(2),
    },
    '& [class*="MuiListItemText-primary"]': {
        fontSize: '1.5rem',
    },
}));

const StyledSubList = styled(List)(({ theme }) => ({
    '& [class*="MuiListItemText-root"]': {
        paddingLeft: theme.spacing(4),
    },
    '& [class*="MuiListItemText-primary"]': {
        fontSize: '1.3rem',
    },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
}));

interface MobileDrawerProps {
    routes: (AppRoute | AppRoute[])[];
    icon: ReactNode;
    extraListItems?: React.ReactNode;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ routes, icon, extraListItems }) => {
    const history = useHistory();
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [listOpen, setListOpen] = useState<number>(
        routes
            .map(route => (Array.isArray(route) ? route[0] : route))
            .findIndex(route =>
                route.path === ROUTE_HOME.path
                    ? false
                    : !!matchPath(history.location.pathname, { path: route.path, exact: false })
            )
    );

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        setDrawerOpen(open);
    };

    const toggleList = (id: number) => () => {
        setListOpen(id === listOpen ? -1 : id);
    };

    const handleRouteClick = ({ path }: AppRoute) => () => {
        history.push(path);
        setDrawerOpen(false);
    };

    const makeListItem = (route: AppRoute, collapseIndex?: number) => (
        <ListItem
            key={route.path}
            button
            onClick={collapseIndex !== undefined ? toggleList(collapseIndex) : handleRouteClick(route)}
            selected={!!matchPath(history.location.pathname, { path: route.path, exact: true })}>
            <ListItemText primary={t(route.t_name, route.t_params)} />
            {route.infoBadgeCount != null && route.infoBadgeCount > 0 && (
                <ListItemAvatar>
                    <StyledChip size="small" label={route.infoBadgeCount} />
                </ListItemAvatar>
            )}
        </ListItem>
    );

    return (
        <>
            <ToolbarItemIconButton aria-label="menu" edge="end" onClick={toggleDrawer(!drawerOpen)}>
                {icon}
            </ToolbarItemIconButton>
            <Drawer anchor="top" open={drawerOpen} onClose={toggleDrawer(false)}>
                <StyledList>
                    {routes.map((route, i) => {
                        if (Array.isArray(route))
                            return (
                                <React.Fragment key={i}>
                                    {makeListItem(route.slice(0, 1)[0], i)}
                                    <Collapse in={i === listOpen} timeout="auto">
                                        <StyledSubList>{route.slice(1).map(r => makeListItem(r))}</StyledSubList>
                                    </Collapse>
                                </React.Fragment>
                            );
                        else return makeListItem(route);
                    })}
                    <Divider />
                    {extraListItems}
                </StyledList>
            </Drawer>
        </>
    );
};

export default MobileDrawer;
